import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  Box,
  Text,
  Badge,
  SimpleGrid,
  Image,
  Spinner,
  Flex,
  Alert,
  AlertIcon,
  List,
  ListItem,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { FiPrinter } from 'react-icons/fi';
import { httpsCallable } from 'firebase/functions';
import { functionsUsEast1 } from '../../utils/init-firebase';
import usePostcardDesigns, { priceForMailClass } from '../../hooks/usePostcardDesigns';

/**
 * Bulk "Send Postcard" modal — shared by the contacts bar (Dashboard/People)
 * and the deals bar (My Deals/Deals workbench).
 *
 * One PCM order for the whole selection via the sendBulkPostcards callable.
 * A requestId is generated once per open, so double-clicking Send (or retrying
 * after a network error) can never double-mail: the backend uses it as PCM's
 * idempotent extRefNbr.
 *
 * @param {'contacts'|'deals'} recipientType
 * @param {string[]} ids selected contact or deal ids
 * @param {function=} onSuccess called with the send result after Done
 */
export default function BulkPostcardModal({ isOpen, onClose, recipientType, ids, onSuccess }) {
  // All hooks unconditionally at the top (Rules of Hooks).
  const { designs, environment, balance, loading, error } = usePostcardDesigns(isOpen);
  const [designId, setDesignId] = useState(null);
  const [mailClass, setMailClass] = useState('FirstClass');
  const [addressSource, setAddressSource] = useState('mailing');
  const [designVariables, setDesignVariables] = useState({});
  const [requestId, setRequestId] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [result, setResult] = useState(null);
  const [sendError, setSendError] = useState(null);
  const toast = useToast();

  const selectedBorder = useColorModeValue('green.400', 'green.300');
  const cardBorder = useColorModeValue('gray.200', 'gray.600');
  const thumbBg = useColorModeValue('gray.100', 'gray.700');
  const skipBg = useColorModeValue('gray.50', 'gray.700');

  // Fresh idempotency key + clean slate each time the modal opens.
  useEffect(() => {
    if (isOpen) {
      setRequestId(
        (typeof crypto !== 'undefined' && crypto.randomUUID)
          ? crypto.randomUUID()
          : `pc${Date.now()}${Math.random().toString(36).slice(2, 10)}`
      );
      setResult(null);
      setSendError(null);
      setIsSending(false);
    }
  }, [isOpen]);

  const selectedDesign = useMemo(
    () => designs.find((d) => d.designID === designId) || null,
    [designs, designId]
  );
  const designFields = selectedDesign?.designFields || [];
  const mailClassOptions = selectedDesign?.mailClasses?.length
    ? selectedDesign.mailClasses
    : ['FirstClass', 'Standard'];
  const unitPrice = selectedDesign ? priceForMailClass(selectedDesign.pricing, mailClass) : null;
  const estimatedCost = unitPrice != null ? unitPrice * (ids?.length || 0) : null;
  const missingMandatory = designFields.filter(
    (f) => f.mandatory && !String(designVariables[f.fieldKey] || '').trim()
  );

  const selectDesign = (d) => {
    setDesignId(d.designID);
    if (d.mailClasses?.length && !d.mailClasses.includes(mailClass)) {
      setMailClass(d.mailClasses[0]);
    }
    // Keep values for fields that still exist on the new design.
    setDesignVariables((prev) => {
      const keep = {};
      (d.designFields || []).forEach((f) => {
        if (prev[f.fieldKey]) keep[f.fieldKey] = prev[f.fieldKey];
      });
      return keep;
    });
  };

  const handleSend = async () => {
    if (!designId || missingMandatory.length || isSending) return;
    setIsSending(true);
    setSendError(null);
    try {
      const fn = httpsCallable(functionsUsEast1, 'sendBulkPostcards');
      const { data } = await fn({
        recipientType,
        ids,
        designId,
        designName: selectedDesign?.friendlyName || null,
        designFields: designFields.map((f) => ({
          fieldKey: f.fieldKey,
          fieldLabel: f.fieldLabel,
          mandatory: f.mandatory === true,
        })),
        mailClass,
        addressSource: recipientType === 'deals' ? addressSource : 'mailing',
        designVariables,
        requestId,
      });
      if (data?.ok) {
        setResult(data);
      } else {
        setSendError(data?.error || 'Send failed');
        if (Array.isArray(data?.skipped) && data.skipped.length) {
          setResult({ ...data, ok: false });
        }
      }
    } catch (err) {
      setSendError(err.message || 'Send failed');
    } finally {
      setIsSending(false);
    }
  };

  const handleDone = () => {
    if (result?.ok) {
      toast({
        title: `Postcard order placed${result.environment === 'sandbox' ? ' (sandbox)' : ''}`,
        description: `${result.sentCount} recipient${result.sentCount === 1 ? '' : 's'}${result.skipped?.length ? `, ${result.skipped.length} skipped` : ''}`,
        status: 'success',
        duration: 5000,
      });
      if (onSuccess) onSuccess(result);
    }
    onClose();
  };

  const showResults = !!result;

  return (
    <Modal isOpen={isOpen} onClose={showResults ? handleDone : onClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack>
            <FiPrinter />
            <Text>Send Postcard</Text>
            {environment === 'sandbox' && (
              <Badge colorScheme="orange">Sandbox — will not actually mail</Badge>
            )}
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {showResults ? (
            <VStack align="stretch" spacing={4}>
              {result.ok ? (
                <Alert status="success" borderRadius="md">
                  <AlertIcon />
                  <Box>
                    <Text fontWeight="semibold">
                      Order placed — {result.sentCount} postcard{result.sentCount === 1 ? '' : 's'}
                      {result.duplicate ? ' (already submitted earlier)' : ''}
                    </Text>
                    <Text fontSize="sm">
                      {result.orderId ? `PCM order #${result.orderId} · ` : ''}
                      {result.environment === 'sandbox' ? 'Sandbox environment — nothing will print or mail.' : 'Production — postcards will be printed and mailed.'}
                    </Text>
                  </Box>
                </Alert>
              ) : (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  {sendError || 'Send failed'}
                </Alert>
              )}
              {Array.isArray(result.skipped) && result.skipped.length > 0 && (
                <Box>
                  <Text fontWeight="semibold" fontSize="sm" mb={1}>
                    Skipped ({result.skipped.length})
                  </Text>
                  <Box maxH="200px" overflowY="auto" bg={skipBg} borderRadius="md" p={2}>
                    <List spacing={1}>
                      {result.skipped.map((s, i) => (
                        <ListItem key={`${s.id}-${i}`} fontSize="sm">
                          <Text as="span" fontWeight="medium">{s.name}</Text>
                          <Text as="span" color="gray.500"> — {s.reason}</Text>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                </Box>
              )}
            </VStack>
          ) : (
            <VStack align="stretch" spacing={4}>
              <Text fontSize="sm" color="gray.500">
                Sending to <b>{(ids?.length || 0).toLocaleString()}</b> selected {recipientType === 'deals' ? 'deal' : 'contact'}{(ids?.length || 0) === 1 ? '' : 's'}.
                Contacts marked Do Not Contact, inactive, or missing a complete address are skipped automatically.
              </Text>

              <FormControl isRequired>
                <FormLabel mb={1}>Postcard Design</FormLabel>
                {loading ? (
                  <Flex justify="center" py={8}><Spinner /></Flex>
                ) : error ? (
                  <Alert status="error" borderRadius="md" fontSize="sm"><AlertIcon />{error}</Alert>
                ) : designs.length === 0 ? (
                  <Text fontSize="sm" color="gray.500">
                    No postcard designs found on the PostcardMania account. Add designs in the PCM portal first.
                  </Text>
                ) : (
                  <Box maxH="300px" overflowY="auto" borderWidth="1px" borderColor={cardBorder} borderRadius="md" p={2}>
                    <SimpleGrid columns={{ base: 2, md: 3 }} spacing={2}>
                      {designs.map((d) => {
                        const price = priceForMailClass(d.pricing, mailClass);
                        const isSelected = designId === d.designID;
                        return (
                          <Box
                            key={d.designID}
                            borderWidth="2px"
                            borderColor={isSelected ? selectedBorder : 'transparent'}
                            borderRadius="md"
                            p={1}
                            cursor="pointer"
                            onClick={() => selectDesign(d)}
                            _hover={{ borderColor: isSelected ? selectedBorder : cardBorder }}
                          >
                            {d.proofFront ? (
                              <Image
                                src={d.proofFront}
                                alt={d.friendlyName}
                                borderRadius="sm"
                                objectFit="cover"
                                w="100%"
                                h="90px"
                                fallback={<Box h="90px" bg={thumbBg} borderRadius="sm" />}
                              />
                            ) : (
                              <Flex h="90px" bg={thumbBg} borderRadius="sm" align="center" justify="center">
                                <Text fontSize="xs" color="gray.500">No preview</Text>
                              </Flex>
                            )}
                            <Text fontSize="xs" fontWeight="medium" noOfLines={1} mt={1}>{d.friendlyName}</Text>
                            <Text fontSize="10px" color="gray.500">
                              {d.size?.label || d.size?.key || ''}
                              {price != null ? ` · from $${price.toFixed(2)}` : ''}
                            </Text>
                          </Box>
                        );
                      })}
                    </SimpleGrid>
                  </Box>
                )}
              </FormControl>

              <HStack spacing={4} align="flex-start">
                <FormControl>
                  <FormLabel>Mail Class</FormLabel>
                  <Select size="sm" value={mailClass} onChange={(e) => setMailClass(e.target.value)}>
                    {mailClassOptions.map((mc) => (
                      <option key={mc} value={mc}>{mc === 'FirstClass' ? 'First Class' : 'Standard'}</option>
                    ))}
                  </Select>
                </FormControl>
                {recipientType === 'deals' && (
                  <FormControl>
                    <FormLabel>Mail To</FormLabel>
                    <Select size="sm" value={addressSource} onChange={(e) => setAddressSource(e.target.value)}>
                      <option value="mailing">Contact's mailing address</option>
                      <option value="property">Deal's property address</option>
                    </Select>
                  </FormControl>
                )}
              </HStack>

              {designFields.length > 0 && (
                <FormControl isInvalid={missingMandatory.length > 0}>
                  <FormLabel mb={1}>Design Fields</FormLabel>
                  <Text fontSize="xs" color="gray.500" mb={2}>
                    Personalize the design. Merge tags like {'{{contact.firstName}}'} are resolved per recipient.
                  </Text>
                  <VStack spacing={3} align="stretch">
                    {designFields.map((f) => (
                      <Box key={f.fieldKey}>
                        <FormLabel mb={0} fontSize="sm">
                          {f.fieldLabel || f.fieldKey}
                          {f.mandatory && <Text as="span" color="red.400"> *</Text>}
                        </FormLabel>
                        <Input
                          size="sm"
                          value={designVariables[f.fieldKey] || ''}
                          onChange={(e) => setDesignVariables((prev) => ({ ...prev, [f.fieldKey]: e.target.value }))}
                          placeholder="e.g. {{contact.firstName}}"
                        />
                      </Box>
                    ))}
                  </VStack>
                  {missingMandatory.length > 0 && (
                    <Text fontSize="xs" color="red.400" mt={1}>
                      Required: {missingMandatory.map((f) => f.fieldLabel || f.fieldKey).join(', ')}
                    </Text>
                  )}
                </FormControl>
              )}

              {sendError && (
                <Alert status="error" borderRadius="md" fontSize="sm">
                  <AlertIcon />
                  {sendError}
                </Alert>
              )}

              <HStack justify="space-between" fontSize="sm" color="gray.500">
                <Text>
                  {estimatedCost != null
                    ? `Estimated cost: ~$${estimatedCost.toFixed(2)} (${ids?.length || 0} × $${unitPrice.toFixed(2)})`
                    : 'Pricing unavailable'}
                </Text>
                {typeof balance === 'number' && <Text>PCM balance: ${balance.toFixed(2)}</Text>}
              </HStack>
            </VStack>
          )}
        </ModalBody>

        <ModalFooter>
          {showResults ? (
            <Button colorScheme="blue" onClick={handleDone}>Done</Button>
          ) : (
            <>
              <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isSending}>
                Cancel
              </Button>
              <Button
                colorScheme="green"
                leftIcon={<FiPrinter />}
                onClick={handleSend}
                isLoading={isSending}
                loadingText="Placing order..."
                isDisabled={!designId || missingMandatory.length > 0 || !(ids?.length)}
              >
                Send {ids?.length || 0} Postcard{(ids?.length || 0) === 1 ? '' : 's'}
              </Button>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
